import { z } from 'zod';

import { MarketStatus } from '@nucleus-commerce/api-client/admin';
import { isMoney } from '@nucleus-commerce/core';

const CURRENCY = /^[A-Z]{3}$/;

export const ROUNDING_MODES = ['nearest', 'up', 'down', 'ending'] as const;
export type RoundingMode = (typeof ROUNDING_MODES)[number];

const money = z.custom<{ amount: number; currency: string }>(isMoney).nullable();

/**
 * Rounding holds both shapes the API knows; the mode says which one the mapper sends:
 * `to` (a money step, e.g. 1.00) for nearest / up / down, `ending` (0–99) for ending.
 */
export const marketFormSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, { error: 'markets:validation.nameRequired' })
      .max(120, { error: 'markets:validation.nameLength' }),
    countries: z.array(z.string()).min(1, { error: 'markets:validation.countriesRequired' }),
    currency: z
      .string()
      .trim()
      .toUpperCase()
      .regex(CURRENCY, { error: 'markets:validation.currency' }),
    status: z.enum(MarketStatus),
    rounding: z.object({
      mode: z.enum(ROUNDING_MODES),
      to: money,
      ending: z
        .string()
        .trim()
        .refine((v) => v === '' || /^\d{1,2}$/.test(v), { error: 'markets:validation.ending' }),
    }),
  })
  .refine(
    (v) => v.rounding.mode === 'ending' || (v.rounding.to !== null && v.rounding.to.amount > 0),
    {
      path: ['rounding', 'to'],
      error: 'markets:validation.roundToRequired',
    },
  )
  .refine((v) => v.rounding.mode !== 'ending' || v.rounding.ending !== '', {
    path: ['rounding', 'ending'],
    error: 'markets:validation.ending',
  });

export type MarketFormInput = z.input<typeof marketFormSchema>;
export type MarketFormOutput = z.output<typeof marketFormSchema>;
